import { Box, styled, Tooltip, useTheme } from '@chronosphereio/chrono-ui';
import { ChevronLeft, ChevronRight, MagnifyMinusOutline } from 'mdi-material-ui';
import { useState } from 'react';
import { useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';
import { CombinedControlsIconButton } from '../CombinedControls';
import { TIME_RANGE_CONTROL_LABELS } from './model';

export function TimeRangeWindowControls() {
  const { absoluteTimeRange, setTimeRange } = useTimeRange();
  const start = absoluteTimeRange.start;
  const end = absoluteTimeRange.end;
  const currentTimeWindow = end.getTime() - start.getTime();
  const [disableForward, setDisableForward] = useState(false);

  const zoomOut = () => {
    // Calculate the new time window to zoom out by, e.g., half of the current time window.
    const zoomFactor = 0.2;
    const timeToZoom = currentTimeWindow * zoomFactor;

    // Check if the end time is "now" (no fixed end time)
    if (end.getTime() === new Date().getTime()) {
      // Expand the start time backward by the calculated time to zoom.
      const newStart = new Date(start.getTime() - timeToZoom);
      setTimeRange({ start: newStart, end });
    } else {
      // Expand both start and end times equidistantly.
      setDisableForward(false);
      const halfTimeToZoom = timeToZoom / 2;
      let newStart = new Date(start.getTime() - halfTimeToZoom);
      let newEnd = new Date(end.getTime() + halfTimeToZoom);

      // Check if the new end date is in the future.
      if (newEnd.getTime() > new Date().getTime()) {
        const adjustment = newEnd.getTime() - new Date().getTime();
        newEnd = new Date();
        newStart = new Date(newStart.getTime() - adjustment);
      }
      setTimeRange({ start: newStart, end: newEnd });
    }
  };

  const pageForward = () => {
    // Calculate the time difference between the new end and current time
    const timeDifference = Math.abs(end.getTime() - new Date().getTime());

    // Exit early if the new end date is within 5 seconds of now
    if (timeDifference <= 5000) {
      setDisableForward(true);
      return; // Do nothing if the new end is too close to now
    }
    setDisableForward(false);

    // Calculate the new end date by adding currentTimeWindow to the current end date
    let newEnd = new Date(end.getTime() + currentTimeWindow);

    // Check if the new end date is in the future
    if (newEnd.getTime() > new Date().getTime()) {
      newEnd = new Date();
      const newStart = new Date(newEnd.getTime() - currentTimeWindow);
      setTimeRange({ start: newStart, end: newEnd });
      return;
    }

    const newStart = new Date(newEnd.getTime() - currentTimeWindow);
    setTimeRange({ start: newStart, end: newEnd });
  };

  const pageBackwards = () => {
    const newStart = new Date(start.getTime() - currentTimeWindow);
    const newEnd = new Date(end.getTime() - currentTimeWindow);
    setDisableForward(false);
    setTimeRange({ start: newStart, end: newEnd });
  };

  const { theme } = useTheme();
  const rebrand = theme.featureFlags.panwRebrandStubYear;

  const shiftButtons = (
    <>
      <Tooltip Content={TIME_RANGE_CONTROL_LABELS.BACKWARD}>
        <ShiftButton
          onClick={pageBackwards}
          aria-label={TIME_RANGE_CONTROL_LABELS.BACKWARD}
          sx={{
            // Make the border transparent when not hovered/focused to match
            // mocks.
            borderRightColor: 'transparent',
          }}
        >
          <ChevronLeft />
        </ShiftButton>
      </Tooltip>

      <Tooltip Content={TIME_RANGE_CONTROL_LABELS.FORWARD}>
        <ShiftButton
          sx={() => {
            const disabledColor = theme.featureFlags.panwRebrandStubYear
              ? theme.palette.text.disabled
              : theme.palette.grey[600];
            const disabledHoverColor = theme.featureFlags.panwRebrandStubYear
              ? theme.palette.text.disabled
              : theme.palette.grey[800];
            return {
              // sc-164282
              // remove this comment once we've migrated to the new palette
              // Make the border transparent when not hovered/focused to match
              // mocks.
              borderLeftColor: 'transparent',
              color: disableForward ? disabledColor : '',
              ':hover': {
                color: disableForward ? disabledHoverColor : '',
              },
            };
          }}
          onClick={pageForward}
          aria-label={TIME_RANGE_CONTROL_LABELS.FORWARD}
        >
          <ChevronRight />
        </ShiftButton>
      </Tooltip>
    </>
  );

  return (
    <>
      {rebrand ? shiftButtons : <ShiftButtonsWrapper>{shiftButtons}</ShiftButtonsWrapper>}
      <Tooltip Content={TIME_RANGE_CONTROL_LABELS.ZOOM_OUT}>
        <CombinedControlsIconButton onClick={zoomOut} aria-label={TIME_RANGE_CONTROL_LABELS.ZOOM_OUT}>
          <MagnifyMinusOutline />
        </CombinedControlsIconButton>
      </Tooltip>
    </>
  );
}

const ShiftButton = styled(CombinedControlsIconButton)(({ theme }) => {
  if (theme.featureFlags.panwRebrandStubYear) {
    return {};
  }

  return {
    // Make the background transparent, so that the transparent borders fall back
    // to the `ShiftButtonsWrapper` and the top/bottom borders it sets to fix the
    // transparent left/right notch.
    background: 'transparent',
    // Negative margin enables the button to sit on top of the
    // borders from `ShiftButtonsWrapper`.
    marginTop: '-1px',
    marginBottom: '-1px',
  };
});

const ShiftButtonsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  // Display full top/bottom border around the nested buttons, so that you do
  // not get a small notch where the buttons meet because we make the center
  // borders where they meet transparent.
  borderTop: `solid 1px ${theme.palette.border.bold}`,
  borderBottom: `solid 1px ${theme.palette.border.bold}`,

  background: theme.palette.background.container,
}));
